import React, { useState } from "react";
import { Box, Typography, TextField, InputAdornment, Pagination, Grid, Button, Chip } from "@mui/material";
import { Search, Clear } from "@mui/icons-material";
import { useIcons } from "../../contexts/IconContext";
import DisplayIcon from "./DisplayIcon";

const IconSelector = ({ selectedIcon, onIconSelect, iconsPerPage = 20 }) => {
  const { icons, isLoading } = useIcons();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  if (isLoading) {
    return <p>Loading icons...</p>;
  }

  // Filter icons based on search term
  const filteredIcons = icons.filter((icon) => 
    icon.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredIcons.length / iconsPerPage);
  const paginatedIcons = filteredIcons.slice((currentPage - 1) * iconsPerPage, currentPage * iconsPerPage);

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  return (
    <Box sx={{ width: "100%" }}>
      {selectedIcon && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Preview Selected Icon
          </Typography>
          <Box sx={{ mb: 3 }}>
            <DisplayIcon 
              name={selectedIcon} 
              height={75} 
              width={75} 
            />
          </Box>
        </>
      )}
      
      <Typography variant="h6" sx={{ mb: 2 }}>
        Select an Icon for this Entity
      </Typography>
      
      {/* Search Controls */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search Icons..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <Button
                  size="small"
                  onClick={handleClearSearch}
                  sx={{ minWidth: "auto", p: 0.5 }}
                >
                  <Clear fontSize="small" />
                </Button>
              </InputAdornment>
            ),
          }}
        />
        
        {/* Search Status */}
        {searchTerm && (
          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Chip 
              label={`Search: "${searchTerm}"`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
            <Chip 
              label={`${filteredIcons.length} of ${icons.length} icons`} 
              size="small" 
              color="secondary" 
              variant="outlined"
            />
            <Button
              size="small"
              onClick={handleClearSearch}
              sx={{ ml: 1 }}
            >
              Clear Search
            </Button>
          </Box>
        )}
      </Box>

      {/* Icons Display */}
      {filteredIcons.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm 
              ? `No icons found matching "${searchTerm}"`
              : "No icons available"
            }
          </Typography>
          {searchTerm && (
            <Button
              variant="contained"
              onClick={handleClearSearch}
              startIcon={<Clear />}
            >
              Show All Icons
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {paginatedIcons.map((icon) => (
              <Grid item xs={3} key={icon.name}>
                <Box
                  onClick={() => onIconSelect(icon.name)}
                  sx={{
                    cursor: "pointer",
                    textAlign: "center",
                    border: selectedIcon === icon.name ? "2px solid #1976d2" : "1px solid #ddd",
                    borderRadius: "8px",
                    padding: "10px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#1976d2",
                      backgroundColor: "#f5f5f5",
                      transform: "translateY(-2px)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                    }
                  }}
                >
                  <img 
                    src={icon.url} 
                    alt={icon.name} 
                    style={{ 
                      width: 50, 
                      height: 50,
                      objectFit: "contain"
                    }} 
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mt: 1,
                      fontSize: "0.75rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {icon.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination 
                count={totalPages} 
                page={currentPage} 
                onChange={handlePageChange} 
                color="primary" 
                size="medium" 
                siblingCount={1} 
                boundaryCount={1} 
              />
            </Box>
          )}
          
          {/* Results Summary */}
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Showing {((currentPage - 1) * iconsPerPage) + 1} - {Math.min(currentPage * iconsPerPage, filteredIcons.length)} of {filteredIcons.length} icons
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default IconSelector;
